import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  displayFlexItem,
  getStatusFlow,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { OTPostApprovalHeader } from "../../Headers";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { StatusRow } from "components/CustomComponents/StatusRow";

export const MyAOvertimeApproval = () => {
  const containerRef = useRef(null);
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [otPreApprovalDetails, setOTPreApprovalDetails] = useState([]);
  const [handleLoader, setHandleLoader] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState("");
  const {
    result: otPreAppData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}My_Zone/Apply/Over_time/ot-post-approval/?user_id=${userDetails?.id}`
  );
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Apply",
    "OT Post-Approval"
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggle = (index, value) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOTPreApprovalDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHandleLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  return !handleLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Overtime Approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and history of overtime payroll requests that are yet to be
            processed or approved for payment.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title={"Overtime Post-Approval Records"}
            filterHeaders={OTPostApprovalHeader}
            data={otPreAppData}
            filteredData={otPreApprovalDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {OTPostApprovalHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {otPreApprovalDetails?.length === 0 ? (
                    <InfoMessage dataLength={otPreAppData} />
                  ) : (
                    otPreApprovalDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value, index) => (
                        <React.Fragment key={index}>
                          <Mui.TableRow sx={tableRowSize}>
                            {OTPostApprovalHeader.map((head) => (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => {
                                  if (head.key === "expand") {
                                    handleToggle(index, value);
                                  }
                                }}
                              >
                                {head.key === "expand" ? (
                                  <ArrowRightIcon
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      cusror: "pointer",
                                    }}
                                  />
                                ) : head.key === "status" ? (
                                  <Mui.Box sx={displayFlexItem}>
                                    <StatusBadge
                                      color={value?.color_code}
                                      label={value?.status || "N/A"}
                                    />
                                  </Mui.Box>
                                ) : (
                                  value[head.key] ?? "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>

                          {expandedIndex === index && (
                            <Mui.TableRow>
                              <Mui.TableCell
                                colSpan={OTPostApprovalHeader.length}
                                sx={{ p: 2 }}
                              >
                                {getStatusFlow(expandedData)[
                                  expandedData?.status
                                ]?.map((item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ))}
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {otPreApprovalDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={otPreApprovalDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
    </React.Fragment>
  );
};
